import React, { useState, useEffect, useRef } from "react";
import {
  API_BASE_URL,
  GOVERNMENT_FEE_MESSAGES,
  getSubmitButtonLabel, getContentUpdateDescription, getSheetUrl,
} from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminGovernmentFeeEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const snapshotRef = useRef(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        setContent(data.section.content);
        snapshotRef.current = JSON.stringify(data.section.content);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(GOVERNMENT_FEE_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(GOVERNMENT_FEE_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl("governmentFee"), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/governmentFee/push-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) { const text = await res.text(); setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`); return; }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/governmentFee/pull-sheet`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const doSubmit = async (submitterNote) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    const submitPayload = { ...content };
    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: { type: "content_update", sectionKey: section.key, content: submitPayload, ...(submitterNote ? { submitterNote } : {}) },
          sectionKey: section.key,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        snapshotRef.current = JSON.stringify(content);
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="AdminEmptyState">{GOVERNMENT_FEE_MESSAGES.loading}</div>;

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>&larr; Back to Content Manager</button>
        <div className="AdminEmptyState">{GOVERNMENT_FEE_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">Edit Government Fee Content</h3>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={handleSubmit} disabled={!isDirty || saving} style={{ whiteSpace: "nowrap" }}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>Google Sheet</button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </button>
        </div>
      </div>

      {message && <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>{message}</p>}

      <div className="AdminCard">
        <div className="AdminDataHeader">
          <h4 className="AdminSectionTitle">Data</h4>
        </div>
        <div className="AdminDataInfo">
          <div>
            <span className="AdminDataLabel">Last Updated</span>
            <p className="AdminDataValue">{sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}</p>
          </div>
          <div>
            <span className="AdminDataLabel">Section Key</span>
            <p className="AdminDataValue">{section?.key || "governmentFee"}</p>
          </div>
        </div>
        <textarea className="AdminJsonEditor" value={JSON.stringify(content, null, 2)}
          onChange={(e) => { try { const parsed = JSON.parse(e.target.value); setContent(parsed); setMessage(""); } catch { setMessage(GOVERNMENT_FEE_MESSAGES.invalidJson); } }} />
      </div>

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="AdminModalTitle" style={{ margin: 0 }}>Describe Your Changes</h3>
              <button className="AdminCloseBtn" onClick={() => setShowNoteModal(false)}>&times;</button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px 0" }}>Please provide a note describing what changes you made. This is required before submitting.</p>
            <textarea className="AdminTextarea" style={{ minHeight: 100, width: "100%", boxSizing: "border-box" }} placeholder="Describe what changed..." value={noteText} onChange={(e) => setNoteText(e.target.value)} autoFocus />
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={handleConfirmSubmit} disabled={!noteText.trim() || saving} style={{ opacity: !noteText.trim() ? 0.5 : 1 }}>{saving ? "Submitting..." : "Continue"}</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent" style={{ maxWidth: 400, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>{successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}</div>
            <h3 className="AdminModalTitle" style={{ marginBottom: 4 }}>{successTitle}</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px 0" }}>{successTitle === "Changes Published" ? "Your content changes have been published live." : "Your change request has been submitted for approval."}</p>
            <button className="AdminButton AdminSubmitBtn" onClick={() => setShowSuccessOverlay(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminGovernmentFeeEditor;